import { IconType } from '@meronex/icons';
import React from 'react';
import { ISelectableState, TBasicRow } from '../../../../store/selection/types';
import { TApiaFilter, TModify } from '../../../../types';
import {
  TAccordionCellButtonRenderer,
  TAccordionCellRenderer,
} from './renderers/AccordionCell';
import { IAccordionElement } from './renderers/AccordionElement';

/**
 * Se encontró en algunos casos particulares que
 * el servidor devuelve Up y Down para referirse
 * al orden actual de una columna.
 */
export type TResponsiveTableSortValue = null | 'A' | 'D' | 'Down' | 'Up';

/**
 * Cada vez que se hace click en el botón del
 * cabezal de una columna, si la columna tiene
 * habilitado el ordenamiento, se emite un
 * evento de este tipo, informando cuál columna
 * emitió el evento, y cuál es el nuevo sortValue.
 */
export type TResponsiveTableSortChangeEvent = {
  name: string;
  column: TResponsiveTableColumn;
  columnIndex: number;
  /**
   * Es el orden que la columna debería tener
   * si no hay ningún motivo para evitar que
   * la acción de ordenamiento se lleve a cabo.
   *
   * Es importante notar que el componente
   * responsiveTable no realiza ninguna acción
   * de ordenamiento por defecto, sino que es
   * quien la usa, quien debe realizar las
   * acciones correspondientes e informarlo a
   * la tabla actualizando los registros y el
   * estado de la columna correspondiente.
   */
  sortValue: TResponsiveTableSortValue;
};

/**
 * Cada celda admite un renderer, que recibe
 * propiedades personalizadas. Este tipo define
 * cuál es la forma de esas propiedades.
 */
export type TResponsiveTableCellRendererProps<
  RendererPropsType extends Partial<
    Record<keyof RendererPropsType, unknown>
  > = Record<string, unknown>,
  HTML = HTMLTableCellElement,
> = {
  cell: TResponsiveTableCell<RendererPropsType>;
  column: TResponsiveTableColumn;
  row: TResponsiveTableRow;
} & React.AllHTMLAttributes<HTML>;

export type TResponsiveTableCellRenderer<
  RendererPropsType extends Partial<
    Record<keyof RendererPropsType, unknown>
  > = Record<string, unknown>,
> = React.FunctionComponent<
  TResponsiveTableCellRendererProps<RendererPropsType>
>;

/**
 * Algunos elementos de la tabla admiten un
 * Renderer personalizado. Dichos elementos
 * extienden este tipo.
 */
export type TResponsiveTableWithRendererElement<
  RendererPropsType extends Partial<
    Record<keyof RendererPropsType, unknown>
  > = Record<string, unknown>,
> = {
  /**
   * Si se desea, el elemento admite un componente
   * personalizado para encargarse del renderizado
   * del mismo en el modo responsivo. De esta forma, se puede proveer
   * de comportamiento específico a cualquier
   * elemento de la tabla.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  AccordionRenderer?: TAccordionCellRenderer<any>;
  /**
   * Son las propiedades que se desean pasar al
   * componente de renderizado personalizado que
   * se haya establecido.
   *
   * Estas props serán obviadas en caso de que
   * no haya componente de renderizado personalizado.
   */
  rendererProps?: RendererPropsType;
  /**
   * Si se desea, el elemento admite un componente
   * personalizado para encargarse del renderizado
   * del mismo. De esta forma, se puede proveer
   * de comportamiento específico a cualquier
   * elemento de la tabla.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Renderer?: TResponsiveTableCellRenderer<any>;
};

export type TResponsiveTableCell<
  RendererPropsType extends Partial<
    Record<keyof RendererPropsType, unknown>
  > = Record<string, unknown>,
> = {
  children?: number | string;
} & TResponsiveTableWithRendererElement<RendererPropsType> &
  Partial<
    Pick<
      HTMLTableCellElement,
      'ariaLabel' | 'className' | 'colSpan' | 'id' | 'rowSpan' | 'title'
    >
  >;

/**
 * Cada celda admite un renderer, que recibe
 * propiedades personalizadas. Este tipo define
 * cuál es la forma de esas propiedades.
 */
export type TResponsiveTableRowRendererProps<
  RendererPropsType extends Partial<
    Record<keyof RendererPropsType, unknown>
  > = Record<string, unknown>,
> = {
  row: TResponsiveTableRow<RendererPropsType>;
  rowIndex: number;
} & React.AllHTMLAttributes<HTMLTableRowElement>;

export type TResponsiveTableRowRenderer<
  RendererPropsType extends Partial<
    Record<keyof RendererPropsType, unknown>
  > = Record<string, unknown>,
> = React.FunctionComponent<
  TResponsiveTableRowRendererProps<RendererPropsType>
>;

export type TResponsiveTableRow<
  RendererPropsType extends Partial<
    Record<keyof RendererPropsType, unknown>
  > = Record<string, unknown>,
> = TBasicRow & {
  /**
   * Si se desea, el elemento admite un componente
   * personalizado para encargarse del renderizado
   * del mismo en el modo responsivo. De esta forma, se puede proveer
   * de comportamiento específico a cualquier
   * elemento de la tabla.
   */

  AccordionButtonRenderer?: TAccordionCellButtonRenderer;
  AccordionElementRenderer?: React.FC<IAccordionElement>;
  additionalProps?: Record<string, unknown>;
  cells: TResponsiveTableCell[];
  separator?: boolean;
  initiallySelected?: boolean;
  renderer?: TResponsiveTableRowRenderer<RendererPropsType>;
  rendererProps?: RendererPropsType;
  rowProps?: RendererPropsType;
  isHidden?: boolean;
  /**
   * Los estados de la fila agregan un ícono en la columna
   * especial que es la que se muestra a la izquierda y
   * contiene, cuando existe, el botón de más información.
   */
  states?: TResponsiveTableRowState[];
} & Partial<
    Pick<
      HTMLTableCellElement,
      'ariaLabel' | 'colSpan' | 'className' | 'rowSpan' | 'title'
    >
  >;

export type TResponsiveTableRowState = {
  /**
   * Es un className que se agregará al elemento <tr>
   */
  className?: string;
  Icon: IconType;
  color?: string;
  tooltip: string;
};

export type TResponsiveTableColumn = TResponsiveTableWithRendererElement & {
  /** Con esta propiedad se puede pasar parámetros opcionales */
  additionalData?: Record<string, unknown>;
  /**
   * Determina si la columna admite ordenamiento.
   */
  allowSorting?: boolean;
  autoAdjustWidth?: boolean;
  children?: React.ReactNode;
  /**
   * Es el índice de columna en el servidor, sirve para comparar a la hora de estabelcer el orden actual.
   */
  colIndex?: number;
  /**
   * Cuando la columna admite ordenamiento, muestra
   * una flecha indicando el orden actual. Esta
   * propiedad establece si la flecha apunta hacia
   * arriba, abajo, o si la flecha es un guión.
   */
  currentSorting?: TResponsiveTableSortValue;
  /** *
   * Valor para decirle al server cuál columna se está ordenando
   */
  dataSortBy?: string;
  hidden?: boolean;
  /**
   * Normalmente, todas las columnas se van a mostrar
   * como renglones en el acordión, cuando este es
   * expandido.
   *
   * Si este flag es pasado en true, la columna
   * correspondiente no se va a mostrar en modo
   * responsivo.
   */
  hideFromAccordion?: boolean;
  /**
   * Elemento id del dom.
   */
  id?: string;
  /**
   * Es el texto que se mostrará en el cabezal.
   */
  label: string;
  maxWidth?: number | string;
  minWidth?: number | string;
  /**
   * El nombre de la columna debe ser único, se utiliza
   * para realizar la asosiación entre las columnas
   * y los filtros, así como con los eventos de orden.
   */
  name: string;
  /**
   * Si se pasa required: true, se pone un asterisco
   * rojo en el cabezal de la columna para señalar que
   * la columna es requerida.
   */
  required?: boolean;
  /**
   * Algunas columnas podrán mostrarse mediante un botón
   * "mostrar más", para ello deberán ser marcadas con
   * esta prop en true.
   */
  showAsAdditional?: boolean;
  /**
   * Cuando no se pasa esta propiedad en ninguna columna,
   * la aplicación encuentra la primera fila sin celdas
   * vacías y la utiliza como título.
   *
   * En cambio, si al menos una columna tiene este flag
   * en true, el título de cada fila del acordión estará
   * compuesto por los valores de estas columnas.
   */
  showInAccordionTitle?: boolean;
  /**
   * Elemento title de html (El tooltip).
   */
  title?: string;
  width?: number | string;
} & Partial<
    Pick<
      HTMLTableCellElement,
      'ariaLabel' | 'className' | 'colSpan' | 'rowSpan'
    >
  >;

export type TResponsiveTableStoreProps = {
  /**
   * Habla sobre el orden de las filas en función de
   * las columnas. Esta propiedad habilita/deshabilita
   * el ordenado de las columnas al hacer click en los
   * cabezales.
   *
   * La tabla admite ordenamiento por defecto, a menos
   * que esta prop venga en false. En caso de que se
   * pase false no importa qué digan las columnas,
   * el ordenamiento no estará habilitado.
   */
  allowSorting?: boolean;

  // Composition
  columns: TResponsiveTableColumn[];
  filters?: TApiaFilter[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows: TResponsiveTableRow<any>[];

  /**
   * Por defecto, la tabla permitirá seleccionar
   * múltiples valores, a menos que esta prop venga
   * en false.
   */
  isMultiple?: boolean;

  label?: string;

  /**
   * Es normal que una tabla tenga paginación y que en cada página haya una
   * cantidad distinta de estados. Si se quiere mantener consistencia en el
   * tamaño de las páginas, es posible reservar un tamaño para una cantidad
   * definida de estados con este parámetro.
   *
   * Se debe pasar un array en donde cada objeto contiene un tooltip obligatorio
   * que será el texto a mostrar cuando se pose el mouse en el cabezal; y
   * un ícono opcional que será mostrado también en el cabezal
   */
  reserveColumnsForStates?: {
    tooltip: string;
    Icon?: IconType;
    iconColor?: string;
  }[];
};

export type TResponsiveTableRowsSelectionEvent = {
  index: number;
  row: TResponsiveTableRow;
}[];

export type TResponsiveTableContextProps = TResponsiveTableStoreProps &
  Pick<
    ISelectableState,
    'allowRowsKeyboardSorting' | 'allowEdition' | 'allowSelection'
  > & {
    /**
     * Cuando se actualiza el array de rows de la tabla
     * desde las props del ResponsiveTableContext, se
     * reinicia el estado de selección de acuerdo a cómo
     * viene definido en el prop. **Si esta prop se pasa
     * en true**, este comportamiento es deshabilitado.
     */
    avoidReparseSelectionOnRowChange?: boolean;
    /**
     * Normalmente, la tabla borra su estado al desmontarse. Esta propiedad evita ese comportamiento y el store no será eliminado.
     */
    avoidTableDestroyOnUnmount?: boolean;
    children: React.ReactNode;
    customLabels?: typeof defaultLabels;
    /**
     * Se puede utilizar este booleano para que la tabla
     * deshabilite las opciones de filtrado y ordenado mientras
     * se está ejecutando una acción ajax.
     */
    isLoading?: boolean;
    /**
     * La tabla debe tener un nombre único que será usado para
     * acceder a las propiedades de la misma en el store. En caso
     * de que no se pase ninguno, la tabla lo generará
     * automaticamente.
     */
    name?: string;

    // Filtering
    onFilterChange?: (ev: TApiaFilter) => void | Promise<boolean>;
    onFilterBlur?: (ev: TApiaFilter) => unknown;
    onFilterPressEnter?: (ev: TApiaFilter) => unknown;
    onFilterError?: (ev: TApiaFilter) => void | Promise<boolean>;
    // Selection
    onChangeSelection?: (ev: TResponsiveTableRowsSelectionEvent) => unknown;
    /**
     * Este evento será llamado cada vez que el usuario esté
     * haciendo foco en la tabla, tenga una selección de
     * filas no vacía y presione la tecla Enter.
     */
    onSelectRows?: (ev: TResponsiveTableRowsSelectionEvent) => unknown;

    // Sorting
    /**
     * Es llamado cada vez que el usuario hace click en el botón
     * del cabezal de la columna. **Importante:** la tabla no
     * realizará ninguna acción de ordenamiento, sino solamente
     * avisar de que el usuario desea que las filas sean ordenadas
     * por alguna columna en particular.
     */
    onSortChange?: (ev: TResponsiveTableSortChangeEvent) => unknown;
    /**
     * Por defecto, la tabla usa variant layout.common.tables.primary
     * y el acordion layout.common.tables.accordion.
     *
     * Estos valores pueden ser modificados con estos parámetros, para
     * usar variantes personalizadas.
     */
    variants?: {
      accordion?: string;
      table?: string;
    };
  };

/**
 * Los callbacks del responsiveTable estarán
 * disponibles a través de un contexto, de
 * forma que cualquier elemento activo que
 * desee informar de un evento en particular
 * pueda hacerlo en forma sencilla.
 */
export type TResponsiveTableContext = TModify<
  Pick<
    TResponsiveTableContextProps,
    | 'name'
    | 'onChangeSelection'
    | 'onFilterBlur'
    | 'onFilterChange'
    | 'onFilterPressEnter'
    | 'onSelectRows'
    | 'onFilterError'
    | 'onSortChange'
    | 'label'
    | 'variants'
  >,
  { labels: typeof defaultLabels; name: string }
>;

export const defaultLabels = {
  noRegisters: window.FINDER_NO_DATA,
};
